import React, { useState, useEffect } from 'react';
import {
  Lightning,
  CheckCircle,
  WarningCircle,
  Clock,
  DownloadSimple,
  GameController,
  ArrowSquareOut,
  SignOut,
  Key,
  User,
  Gauge,
  House,
  Gear,
  DiscordLogo,
  Copy,
  Check,
  Info,
  X,
  ShoppingCart,
  Desktop,
  ArrowClockwise,
  FileText,
  GraduationCap
} from '@phosphor-icons/react';
import { api } from '../utils/api';
import TermsOfService from './TermsOfService';
import './Dashboard.css';

interface DashboardProps {
  username: string;
  onLogout: () => void;
  onLicenseClaimed?: () => void;
  onRedoTutorial?: () => void;
}

interface VoltStatus {
  updateStatus: boolean;
  updatedDate: string;
  rbxversion: string;
  uncPercentage: number;
  suncPercentage: number;
  discordlink: string;
  sunc: {
    suncScrap: string;
    suncKey: string;
  };
}

interface VoltVersions {
  dll: string;
  ui: string;
  emulator: string;
  bin: string;
  offsets: string;
  json: string;
}

interface UserInfo {
  username: string;
  email: string;
  role: string;
  createdAt: string;
  subscription?: {
    id: string;
    plan: string;
    planCode: string;
    status: string;
    startsAt: string;
    expiresAt: string;
    licenseKey?: string;
  };
}

type TabType = 'home' | 'account' | 'settings';

const Dashboard: React.FC<DashboardProps> = ({ username, onLogout, onLicenseClaimed, onRedoTutorial }) => {
  const [activeTab, setActiveTab] = useState<TabType>('home');
  const [voltStatus, setVoltStatus] = useState<VoltStatus | null>(null);
  const [voltVersions, setVoltVersions] = useState<VoltVersions | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedVersion, setCopiedVersion] = useState<string | null>(null);
  
  const [licenseKey, setLicenseKey] = useState('');
  const [claimStatus, setClaimStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [claimMessage, setClaimMessage] = useState('');
  const [showSuncModal, setShowSuncModal] = useState(false);
  const [showClaimOverlay, setShowClaimOverlay] = useState(false);
  const [claimProgress, setClaimProgress] = useState(0);
  const [claimStage, setClaimStage] = useState<'verifying' | 'activating' | 'success' | 'error'>('verifying');
  const [claimErrorMessage, setClaimErrorMessage] = useState('');
  
  // HWID Reset state
  const [hwidStatus, setHwidStatus] = useState<{
    canReset: boolean;
    nextResetAt: string | null;
    remainingHours: number | null;
  } | null>(null);
  const [hwidResetting, setHwidResetting] = useState(false);
  const [hwidMessage, setHwidMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showTOS, setShowTOS] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, versionRes] = await Promise.all([
          fetch('https://proxy-api-taupe.vercel.app/api/status/exploits/volt'),
          fetch('https://api.volt.bz/version/latest')
        ]);
        
        const statusData = await statusRes.json();
        const versionData = await versionRes.json();
        
        setVoltStatus({
          updateStatus: statusData.updateStatus,
          updatedDate: statusData.updatedDate,
          rbxversion: statusData.rbxversion,
          uncPercentage: statusData.uncPercentage,
          suncPercentage: statusData.suncPercentage,
          discordlink: statusData.discordlink,
          sunc: statusData.sunc
        });
        
        setVoltVersions(versionData);

        try {
          // Fetch user info
          const userRes = await api.get('https://api.volt.bz/users/me');
          if (userRes.ok) {
            const userData = await userRes.json();
            
            // Fetch subscription data
            const subRes = await api.get('https://api.volt.bz/licenses/subscriptions');
            if (subRes.ok) {
              const subData = await subRes.json();
              const activeSub = subData.data?.[0]; // Get first subscription
              
              if (activeSub) {
                // Fetch effective end date
                const effectiveRes = await api.get(`https://api.volt.bz/licenses/${activeSub.id}/effective`);
                const effectiveData = effectiveRes.ok ? await effectiveRes.json() : null;
                
                // Fetch license keys
                const keysRes = await api.get(`https://api.volt.bz/licenses/${activeSub.id}/keys`);
                const keysData = keysRes.ok ? await keysRes.json() : null;
                const licenseKey = keysData?.value?.[0]?.key;
                
                userData.subscription = {
                  id: activeSub.id,
                  plan: activeSub.plan?.name || activeSub.product?.name || 'Volt',
                  planCode: activeSub.plan?.code || keysData?.value?.[0]?.plan?.code || 'weekly',
                  status: activeSub.status,
                  startsAt: activeSub.startsAt,
                  expiresAt: effectiveData?.effectiveEndAt || activeSub.baseEndAt,
                  licenseKey: licenseKey
                };
              }
            }
            
            setUserInfo(userData);
          }
          
          // Fetch HWID reset status
          try {
            const hwidRes = await api.get('https://api.volt.bz/users/reset-hwid/status');
            if (hwidRes.ok) {
              const hwidData = await hwidRes.json();
              setHwidStatus(hwidData);
            }
          } catch {
            // HWID status fetch failed silently
          }
        } catch {
          // User info fetch failed silently
        }
      } catch {
        // Data fetch failed silently
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedVersion(key);
    setTimeout(() => setCopiedVersion(null), 1500);
  };

  const resetHwid = async () => {
    if (!hwidStatus?.canReset || hwidResetting) return;
    
    setHwidResetting(true);
    setHwidMessage(null);
    
    try {
      const res = await api.post('https://api.volt.bz/users/reset-hwid', {});
      const data = await res.json();
      
      if (res.ok) {
        setHwidMessage({ type: 'success', text: 'HWID reset successfully!' });
        setHwidStatus({
          canReset: false,
          nextResetAt: data.nextResetAt,
          remainingHours: 24
        });
      } else {
        setHwidMessage({ type: 'error', text: data.message || 'Failed to reset HWID' });
        if (data.nextResetAt) {
          setHwidStatus({
            canReset: false,
            nextResetAt: data.nextResetAt,
            remainingHours: data.remainingHours || 24
          });
        }
      }
    } catch {
      setHwidMessage({ type: 'error', text: 'Connection failed' });
    } finally {
      setHwidResetting(false);
      setTimeout(() => setHwidMessage(null), 3000);
    }
  };

  const formatTimeRemaining = (nextResetAt: string) => {
    const now = new Date();
    const resetTime = new Date(nextResetAt);
    const diff = resetTime.getTime() - now.getTime();
    
    if (diff <= 0) return 'Available now';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  const claimLicense = async () => {
    if (!licenseKey.trim()) return;
    
    // Show overlay and reset progress
    setShowClaimOverlay(true);
    setClaimProgress(0);
    setClaimStage('verifying');
    setClaimStatus('loading');
    
    // Animate progress - Verifying stage (0-50%)
    let progress = 0;
    const verifyInterval = setInterval(() => {
      progress += Math.random() * 8 + 2;
      if (progress >= 50) {
        progress = 50;
        clearInterval(verifyInterval);
        setClaimStage('activating');
        
        // Activating stage (50-90%)
        const activateInterval = setInterval(() => {
          progress += Math.random() * 6 + 1;
          if (progress >= 90) {
            progress = 90;
            clearInterval(activateInterval);
          }
          setClaimProgress(Math.min(90, Math.round(progress)));
        }, 100);
        
        // Make actual API call
        api.post('https://api.volt.bz/licenses/claim', { key: licenseKey })
          .then(async (res) => {
            clearInterval(activateInterval);
            const data = await res.json();
            
            if (res.ok) {
              // Animate to 100% then show success
              setClaimProgress(100);
              await new Promise(resolve => setTimeout(resolve, 500));
              setClaimStage('success');
              setClaimStatus('success');
              setClaimMessage('License claimed successfully!');
              
              // Refresh user info
              const userRes = await api.get('https://api.volt.bz/users/me');
              if (userRes.ok) setUserInfo(await userRes.json());
              
              // Hide overlay and trigger main splash
              setTimeout(() => {
                setShowClaimOverlay(false);
                setLicenseKey('');
                if (onLicenseClaimed) {
                  onLicenseClaimed();
                }
              }, 2500);
            } else {
              setClaimStage('error');
              setClaimErrorMessage(data.message || 'Invalid license key');
              setClaimStatus('error');
              setClaimMessage(data.message || 'Invalid license key');
              setTimeout(() => {
                setShowClaimOverlay(false);
              }, 2500);
            }
          })
          .catch(() => {
            clearInterval(activateInterval);
            setClaimStage('error');
            setClaimErrorMessage('Connection failed');
            setClaimStatus('error');
            setClaimMessage('Connection failed');
            setTimeout(() => {
              setShowClaimOverlay(false);
            }, 2500);
          });
      }
      setClaimProgress(Math.round(progress));
    }, 100);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', month: 'short', day: 'numeric' 
      });
    } catch { return dateString; }
  };

  const getDaysRemaining = (dateString: string) => {
    try {
      const diff = new Date(dateString).getTime() - new Date().getTime();
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    } catch { return 0; }
  };

  return (
    <div className="dashboard">
      {/* Main Content Area */}
      <main className="dashboard-main">
        {loading ? (
          <div className="loading-screen">
            <div className="loader"></div>
            <span>Loading...</span>
          </div>
        ) : (
          <>
            {/* HOME TAB */}
            {activeTab === 'home' && (
              <div className="tab-content">
                {/* Status Hero */}
                <div className={`status-hero ${voltStatus?.updateStatus ? 'working' : 'outdated'}`}>
                  <div className="status-icon">
                    <img src="https://volt.bz/bolt.svg" alt="Volt" className="volt-logo" />
                  </div>
                  <div className="status-content">
                    <div className="status-badge">
                      <span className="status-dot"></span>
                      <span>{voltStatus?.updateStatus ? 'Working' : 'Outdated'}</span>
                    </div>
                    <h1 className="status-title">Volt</h1>
                    <p className="status-updated">
                      <Clock weight="bold" />
                      Last updated: {voltStatus?.updatedDate || 'N/A'}
                    </p>
                  </div>
                  <div className="status-actions">
                    <button className="btn-download" onClick={() => window.open('https://api.volt.bz/version/download/ui', '_blank')}>
                      <DownloadSimple weight="bold" />
                      Download
                    </button>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="stats-row">
                  <div className="stat-card">
                    <div className="stat-label">UNC</div>
                    <div className="stat-value purple">{voltStatus?.uncPercentage || 0}%</div>
                    <div className="stat-bar">
                      <div className="stat-fill purple" style={{ width: `${voltStatus?.uncPercentage || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="stat-card clickable" onClick={() => setShowSuncModal(true)}>
                    <div className="stat-label">sUNC</div>
                    <div className="stat-value blue">{voltStatus?.suncPercentage || 0}%</div>
                    <div className="stat-bar">
                      <div className="stat-fill blue" style={{ width: `${voltStatus?.suncPercentage || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Roblox</div>
                    <div className="stat-value mono">{voltStatus?.rbxversion || 'N/A'}</div>
                    <button className="stat-btn" onClick={() => window.open(`https://rdd.weao.gg/?channel=LIVE&binaryType=WindowsPlayer&version=${voltStatus?.rbxversion}`, '_blank')}>
                      <GameController weight="bold" /> Force Version
                    </button>
                  </div>
                </div>

                {/* Version Info */}
                <div className="section">
                  <div className="section-header">
                    <Info weight="fill" />
                    <h2>Version Info</h2>
                  </div>
                  <div className="version-grid">
                    {[
                      { label: 'Build (DLL)', value: voltVersions?.dll, key: 'dll' },
                      { label: 'UI', value: voltVersions?.ui, key: 'ui' },
                      { label: 'Emulator', value: voltVersions?.emulator, key: 'emu' },
                      { label: 'Bin', value: voltVersions?.bin, key: 'bin' },
                      { label: 'Offsets', value: voltVersions?.offsets, key: 'off' },
                      { label: 'JSON', value: voltVersions?.json, key: 'json' },
                    ].map(({ label, value, key }) => (
                      <div className="version-item" key={key}>
                        <span className="version-label">{label}</span>
                        <div className="version-value">
                          <code>{value || 'N/A'}</code>
                          <button className="copy-btn" onClick={() => copyToClipboard(value || '', key)}>
                            {copiedVersion === key ? <Check weight="bold" /> : <Copy weight="bold" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Links */}
                <div className="section">
                  <div className="section-header">
                    <DiscordLogo weight="fill" />
                    <h2>Community</h2>
                  </div>
                  <div className="links-row two-col">
                    <a href={voltStatus?.discordlink || '#'} target="_blank" rel="noopener noreferrer" className="link-card">
                      <DiscordLogo weight="fill" />
                      <span>Discord</span>
                    </a>
                    <a href="https://discord.gg/6nEEDFZwhr" target="_blank" rel="noopener noreferrer" className="link-card">
                      <DiscordLogo weight="fill" />
                      <span>Support</span>
                    </a>
                  </div>
                </div>

                {/* Tools & Purchase */}
                <div className="section">
                  <div className="section-header">
                    <ArrowSquareOut weight="fill" />
                    <h2>Quick Links</h2>
                  </div>
                  <div className="links-row two-col">
                    <div className="link-card" onClick={() => setShowSuncModal(true)} style={{ cursor: 'pointer' }}>
                      <Gauge weight="fill" />
                      <span>sUNC Results</span>
                    </div>
                    <a href="https://buyvolt.xyz" target="_blank" rel="noopener noreferrer" className="link-card buy">
                      <ShoppingCart weight="fill" />
                      <span>Buy License</span>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* ACCOUNT TAB */}
            {activeTab === 'account' && (
              <div className="tab-content">
                <div className="page-header">
                  <User weight="fill" />
                  <h1>Account</h1>
                </div>

                {/* User Info Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>Profile</h3>
                    <span className="role-badge">{userInfo?.role || 'User'}</span>
                  </div>
                  <div className="info-list">
                    <div className="info-row">
                      <span className="info-label">Username</span>
                      <span className="info-value">{userInfo?.username || username}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Email</span>
                      <span className="info-value">{userInfo?.email || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Subscription Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>Subscription</h3>
                    {userInfo?.subscription ? (
                      <span className={`status-pill ${userInfo.subscription.status === 'active' ? 'active' : 'inactive'}`}>
                        {userInfo.subscription.status?.toUpperCase() || 'ACTIVE'}
                      </span>
                    ) : (
                      <span className="status-pill inactive">NO LICENSE</span>
                    )}
                  </div>
                  {userInfo?.subscription ? (
                    <div className="subscription-info">
                      <div className="plan-name">{userInfo.subscription.plan}</div>
                      {userInfo.subscription.expiresAt && (
                        <div className="plan-expiry">
                          <Clock weight="bold" />
                          Expires {formatDate(userInfo.subscription.expiresAt)}
                          <span className="days-badge">
                            {getDaysRemaining(userInfo.subscription.expiresAt)} days
                          </span>
                        </div>
                      )}
                      {userInfo.subscription.licenseKey && (
                        <div className="license-key-row">
                          <Key weight="bold" />
                          <span className="license-key-value">{userInfo.subscription.licenseKey}</span>
                          <button 
                            className="copy-btn-small"
                            onClick={() => copyToClipboard(userInfo.subscription!.licenseKey!, 'license')}
                          >
                            {copiedVersion === 'license' ? <CheckCircle weight="fill" /> : <Copy weight="bold" />}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="subscription-info">
                      <div className="plan-name no-license">No active subscription</div>
                      <p className="no-license-hint">Claim a license key below to activate</p>
                    </div>
                  )}
                </div>

                {/* HWID Reset Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>HWID Reset</h3>
                    <Desktop weight="fill" />
                  </div>
                  <div className="hwid-section">
                    <p className="hwid-description">
                      Reset your hardware ID to use Volt on a different device. You can reset once every 24 hours.
                    </p>
                    {hwidStatus && !hwidStatus.canReset && hwidStatus.nextResetAt && (
                      <div className="hwid-cooldown">
                        <Clock weight="bold" />
                        <span>{formatTimeRemaining(hwidStatus.nextResetAt)}</span>
                      </div>
                    )}
                    {hwidMessage && (
                      <div className={`message ${hwidMessage.type}`}>
                        {hwidMessage.type === 'success' ? <CheckCircle weight="fill" /> : <WarningCircle weight="fill" />}
                        {hwidMessage.text}
                      </div>
                    )}
                    <button 
                      className="btn-hwid-reset" 
                      onClick={resetHwid} 
                      disabled={!hwidStatus?.canReset || hwidResetting}
                    >
                      <ArrowClockwise weight="bold" className={hwidResetting ? 'spinning' : ''} />
                      {hwidResetting ? 'Resetting...' : hwidStatus?.canReset ? 'Reset HWID' : 'On Cooldown'}
                    </button>
                  </div>
                </div>

                {/* Claim License Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>Claim License</h3>
                    <Key weight="fill" />
                  </div>
                  <div className="license-section">
                    <input
                      type="text"
                      className="input"
                      placeholder="Enter license key..."
                      value={licenseKey}
                      onChange={(e) => {
                        setLicenseKey(e.target.value.toUpperCase());
                        if (claimStatus !== 'idle') setClaimStatus('idle');
                      }}
                      disabled={claimStatus === 'loading'}
                    />
                    {claimStatus !== 'idle' && claimStatus !== 'loading' && (
                      <div className={`message ${claimStatus}`}>
                        {claimStatus === 'success' ? <CheckCircle weight="fill" /> : <WarningCircle weight="fill" />}
                        {claimMessage}
                      </div>
                    )}
                    <button className="btn-primary" onClick={claimLicense} disabled={claimStatus === 'loading' || !licenseKey.trim()}>
                      {claimStatus === 'loading' ? 'Claiming...' : 'Claim License'}
                    </button>
                  </div>
                </div>

                {/* Logout */}
                <button className="btn-logout" onClick={onLogout}>
                  <SignOut weight="bold" />
                  Logout
                </button>
              </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <div className="tab-content">
                <div className="page-header">
                  <Gear weight="fill" />
                  <h1>Settings</h1>
                </div>

                {/* Tutorial Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>Tutorial</h3>
                    <GraduationCap weight="fill" />
                  </div>
                  <div className="settings-section">
                    <p className="settings-description">
                      Need a refresher? Redo the tutorial to learn about Volt's features and setup.
                    </p>
                    <button className="btn-secondary" onClick={onRedoTutorial}>
                      <GraduationCap weight="bold" />
                      Redo Tutorial
                    </button>
                  </div>
                </div>

                {/* Legal Card */}
                <div className="card">
                  <div className="card-header">
                    <h3>Legal</h3>
                    <FileText weight="fill" />
                  </div>
                  <div className="settings-section">
                    <button className="settings-link-btn" onClick={() => setShowTOS(true)}>
                      <FileText weight="bold" />
                      Terms of Service
                      <ArrowSquareOut weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Floating Bottom Navigation */}
      <nav className="bottom-nav">
        <div className="nav-container">
          <div className="nav-indicator" style={{ left: activeTab === 'home' ? '0.5rem' : activeTab === 'account' ? 'calc(0.5rem + 72px)' : 'calc(0.5rem + 144px)' }} />
          <button 
            className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
          >
            <House weight={activeTab === 'home' ? 'fill' : 'regular'} />
            <span>Home</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <User weight={activeTab === 'account' ? 'fill' : 'regular'} />
            <span>Account</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Gear weight={activeTab === 'settings' ? 'fill' : 'regular'} />
            <span>Settings</span>
          </button>
        </div>
      </nav>

      {/* sUNC Modal */}
      {showSuncModal && (
        <div className="modal-overlay" onClick={() => setShowSuncModal(false)}>
          <div className="modal-content sunc-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close floating" onClick={() => setShowSuncModal(false)}>
              <X weight="bold" />
            </button>
            <div className="modal-body">
              {voltStatus?.sunc ? (
                <iframe
                  id="sunc-widget"
                  src="https://sunc.rubis.app/widget/"
                  title="sUNC Results"
                  className="sunc-iframe"
                  allowFullScreen
                  onLoad={(e) => {
                    const iframe = e.target as HTMLIFrameElement;
                    // Load the scrap data (uses default theme which looks great)
                    iframe.contentWindow?.postMessage({
                      type: "sunc-widget:loadScrap",
                      payload: {
                        scrapId: voltStatus.sunc.suncScrap,
                        key: voltStatus.sunc.suncKey
                      }
                    }, "https://sunc.rubis.app");
                  }}
                />
              ) : (
                <p className="modal-empty">sUNC data not available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* License Claim Overlay */}
      {showClaimOverlay && (
        <div className="license-claim-overlay">
          <div className="claim-overlay-content">
            {claimStage !== 'success' && claimStage !== 'error' ? (
              <>
                <div className="claim-logo-container">
                  <img src="https://volt.bz/bolt.svg" alt="Volt" className="claim-logo" />
                  <div className="claim-logo-ring" />
                  <div className="claim-logo-ring delay" />
                </div>
                <div className="claim-status">
                  <span className="claim-status-text">
                    {claimStage === 'verifying' ? 'Verifying License Key...' : 'Activating License...'}
                  </span>
                </div>
                <div className="claim-progress-container">
                  <div className="claim-progress-bar">
                    <div className="claim-progress-fill" style={{ width: `${claimProgress}%` }} />
                  </div>
                  <div className="claim-progress-text">{claimProgress}%</div>
                </div>
                <div className="claim-key-display">
                  <Key weight="bold" />
                  <span>{licenseKey}</span>
                </div>
              </>
            ) : claimStage === 'success' ? (
              <div className="claim-success">
                <div className="success-circle">
                  <CheckCircle weight="fill" />
                </div>
                <div className="success-particles">
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                  <div className="success-particle"></div>
                </div>
                <h2 className="claim-success-title">License Activated!</h2>
                <p className="claim-success-subtitle">Your license has been successfully claimed</p>
                <div className="claim-success-key">
                  <Key weight="bold" />
                  <span>{licenseKey}</span>
                </div>
              </div>
            ) : (
              <div className="claim-error">
                <div className="error-circle">
                  <WarningCircle weight="fill" />
                </div>
                <h2 className="claim-error-title">Claim Failed</h2>
                <p className="claim-error-subtitle">{claimErrorMessage}</p>
                <div className="claim-error-key">
                  <Key weight="bold" />
                  <span>{licenseKey}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTOS && <TermsOfService onClose={() => setShowTOS(false)} />}
    </div>
  );
};

export default Dashboard;
